import { useState } from "react";

import { Button, type Column, EmptyState, Modal, Pill, Table } from "@/components/common";
import { useSubjectRequests, useUpdateSubjectRequest } from "@/hooks/useLgpd";
import type { SubjectRequest, SubjectRequestStatus, SubjectRequestType } from "@/types/lgpd";

const TYPE_LABEL: Record<SubjectRequestType, string> = {
  access: "Acesso",
  correction: "Correção",
  anonymization: "Anonimização",
  deletion: "Exclusão",
  portability: "Portabilidade",
  consent_revocation: "Revogar consentimento",
  sharing_information: "Compartilhamento",
};

const STATUS_LABEL: Record<SubjectRequestStatus, string> = {
  received: "Recebida",
  under_review: "Em análise",
  fulfilled: "Atendida",
  denied: "Negada",
  partially_fulfilled: "Parcial",
};

const STATUS_TONE: Record<SubjectRequestStatus, "diag" | "onb" | "ativa" | "alerta" | "inad"> = {
  received: "diag",
  under_review: "onb",
  fulfilled: "ativa",
  denied: "alerta",
  partially_fulfilled: "inad",
};

function date(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

export function SubjectRequestsPanel() {
  const { data: requests = [], isLoading } = useSubjectRequests();
  const [selected, setSelected] = useState<SubjectRequest | null>(null);

  const columns: Column<SubjectRequest>[] = [
    { id: "protocol", header: "Protocolo", cell: (r) => <span className="cnpj">{r.protocol}</span> },
    { id: "type", header: "Tipo", cell: (r) => <span className="td-muted">{TYPE_LABEL[r.type]}</span> },
    { id: "email", header: "Titular", cell: (r) => <span className="td-faded">{r.email_masked}</span> },
    {
      id: "status",
      header: "Status",
      cell: (r) => <Pill tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Pill>,
    },
    { id: "deadline", header: "Prazo", cell: (r) => <span className="td-faded">{date(r.response_deadline)}</span> },
  ];

  return (
    <>
      <Table
        rows={requests}
        columns={columns}
        getKey={(r) => String(r.id)}
        onRowClick={setSelected}
        empty={
          <EmptyState
            icon="shield-check"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhuma solicitação"}
            description={isLoading ? undefined : "Solicitações de titulares (Art. 18) aparecerão aqui."}
          />
        }
      />
      {selected && <RequestModal request={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function RequestModal({ request, onClose }: { request: SubjectRequest; onClose: () => void }) {
  const update = useUpdateSubjectRequest();
  const [denialReason, setDenialReason] = useState("");
  const terminal = request.status === "fulfilled";

  async function transition(newStatus: SubjectRequestStatus) {
    if ((newStatus === "denied" || newStatus === "partially_fulfilled") && !denialReason.trim()) {
      return;
    }
    await update.mutateAsync({
      id: request.id,
      payload: {
        new_status: newStatus as "under_review" | "fulfilled" | "denied" | "partially_fulfilled",
        denial_reason: denialReason.trim() || undefined,
      },
    });
    onClose();
  }

  return (
    <Modal
      onClose={onClose}
      title={request.protocol}
      subtitle={`${TYPE_LABEL[request.type]} · ${request.email_masked}`}
      size="md"
    >
      <div className="lgpd-detail">
        <div className="lgpd-detail-row">
          <span>Status atual</span>
          <Pill tone={STATUS_TONE[request.status]}>{STATUS_LABEL[request.status]}</Pill>
        </div>
        <div className="lgpd-detail-row">
          <span>Prazo de resposta</span>
          <strong>{date(request.response_deadline)}</strong>
        </div>
        {request.description && (
          <div className="lgpd-detail-block">
            <span>Descrição</span>
            <p>{request.description}</p>
          </div>
        )}
        {request.denial_reason && (
          <div className="lgpd-detail-block">
            <span>Motivo da negativa</span>
            <p>{request.denial_reason}</p>
          </div>
        )}
        {request.response_payload && (
          <div className="lgpd-detail-block">
            <span>Resultado</span>
            <pre className="lgpd-json">{JSON.stringify(request.response_payload, null, 2)}</pre>
          </div>
        )}

        {terminal ? (
          <p className="td-faded">Solicitação atendida — não pode mais ser alterada.</p>
        ) : (
          <>
            <label className="lgpd-field">
              <span>Motivo (obrigatório para negar / parcial)</span>
              <textarea
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                rows={2}
                maxLength={2000}
              />
            </label>
            <div className="lgpd-actions">
              <Button variant="ghost" onClick={() => void transition("under_review")} disabled={update.isPending}>
                Em análise
              </Button>
              <Button
                variant="ghost"
                onClick={() => void transition("partially_fulfilled")}
                disabled={update.isPending || !denialReason.trim()}
              >
                Parcial
              </Button>
              <Button
                variant="danger"
                onClick={() => void transition("denied")}
                disabled={update.isPending || !denialReason.trim()}
              >
                Negar
              </Button>
              <Button variant="primary" onClick={() => void transition("fulfilled")} disabled={update.isPending}>
                Atender
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
